import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { LogOut, CreditCard, ChevronDown, CalendarClock, UserCog, School, LayoutDashboard, ShieldAlert, Sparkles } from 'lucide-react';
import ProfileModal from './ProfileModal';

function formatRenewalDate(isoStr) {
  if (!isoStr) return null;
  try {
    return new Date(isoStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
  } catch {
    return null;
  }
}

export default function Header({ onShowPricing, onStartTour }) { // NUEVO: onStartTour
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);
  const [avatar, setAvatar] = useState('👨‍🏫');

  useEffect(() => {
    if (user?.id) {
      const savedAvatar = localStorage.getItem(`avatar_${user.id}`);
      if (savedAvatar) setAvatar(savedAvatar);
    }
  }, [user, showProfile]);

  const creditsUnlimited = user?.credits_remaining === -1;
  const creditsRemaining = user?.credits_remaining ?? 0;
  const creditsTotal = user?.credits_total ?? 3;
  const creditsPercent = creditsUnlimited ? 100 : creditsTotal > 0 ? (creditsRemaining / creditsTotal) * 100 : 0;
  const creditDot = creditsUnlimited ? 'bg-blue-500' : creditsPercent > 40 ? 'bg-blue-500' : creditsPercent > 10 ? 'bg-yellow-500' : 'bg-red-500';
  const renewalDate = !creditsUnlimited ? formatRenewalDate(user?.credits_reset_date) : null;

  const planColors = {
    ultra: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    gold: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    pro: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
    standard: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    free: 'bg-slate-700 text-slate-300 border-slate-600',
    institution_pro: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    institution_gold: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    institution_ultra: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  };

  const planLabel = { 
    ultra: 'Ultra', gold: 'Gold', pro: 'Pro', standard: 'Standard', free: 'Gratis',
    institution_pro: 'Licencia Pro', institution_gold: 'Licencia Gold', institution_ultra: 'Licencia Ultra'
  };

  return (
    <>
      <header className="h-14 flex-shrink-0 sticky top-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800 flex items-center justify-between px-4 lg:px-6">
        {/* Logo */}
        <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
          <img
            src="/logo.png"
            alt="DinamiClass"
            className="h-14 w-auto object-contain scale-150 origin-left ml-4"
          />
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-2">
          
          {/* Credit meter + renewal badge */}
          <button
            data-testid="credit-meter"
            onClick={onShowPricing}
            className="bg-slate-800 border border-slate-700 rounded-full px-3 py-1.5 flex items-center gap-2 hover:border-slate-600 transition-colors"
          >
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${creditDot}`} />
            <span className="text-sm text-slate-300 font-medium">
              {creditsUnlimited ? 'Ilimitado' : `${creditsRemaining}/${creditsTotal}`}
            </span>
            <span className="text-xs text-slate-500 hidden sm:block">créditos</span>
            {renewalDate && (
              <span
                data-testid="credit-renewal-date"
                className="hidden md:flex items-center gap-1 text-xs text-slate-600 border-l border-slate-700 pl-2"
              >
                <CalendarClock size={11} strokeWidth={1.5} />
                {renewalDate}
              </span>
            )}
          </button>

          {/* Badge Institucional vs Badge de Plan Normal */}
          {user?.institution_name ? (
            <span className="hidden sm:flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border bg-emerald-500/10 text-emerald-400 border-emerald-500/30 font-bold tracking-wide shadow-sm shadow-emerald-900/20">
              {user?.institution_logo ? (
                <img src={user.institution_logo} alt="Logo" className="w-3.5 h-3.5 rounded-full object-cover" />
              ) : (
                <School size={12} strokeWidth={2.5} />
              )}
              {user.institution_name}
            </span>
          ) : (
            <span className={`hidden sm:block text-xs px-2.5 py-1 rounded-full border font-medium tracking-wide ${planColors[user?.plan] || planColors.free}`}>
              {planLabel[user?.plan] || 'Gratis'}
            </span>
          )}

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-btn"
                className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-full px-2.5 py-1.5 transition-colors ml-1"
              >
                <div className="w-6 h-6 rounded-full bg-slate-900 border border-slate-700 flex items-center justify-center text-sm flex-shrink-0 shadow-inner">
                  {avatar}
                </div>
                <span className="text-sm text-slate-300 hidden sm:block max-w-28 truncate font-medium">
                  {user?.name?.split(' ')[0] || 'Docente'}
                </span>
                <ChevronDown size={14} className="text-slate-500" strokeWidth={1.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 text-slate-200 w-56 mt-1 shadow-2xl">
              
              <div className="px-3 py-2.5">
                <p className="text-sm font-bold text-white truncate">{user?.name || 'Docente'}</p>
                <p className="text-xs text-slate-400 truncate mt-0.5">{user?.email}</p>
                
                {user?.institution_name && (
                  <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest mt-2 flex items-center gap-1 bg-emerald-500/10 w-fit px-2 py-0.5 rounded border border-emerald-500/20">
                    <School size={10} /> {user.institution_name}
                  </p>
                )}

                {renewalDate && !user?.institution_name && (
                  <p className="text-[10px] text-slate-500 mt-2 flex items-center gap-1 font-medium tracking-wide">
                    <CalendarClock size={10} strokeWidth={1.5} className="text-blue-400" />
                    RENUEVA EL {renewalDate}
                  </p>
                )}
              </div>
              
              <DropdownMenuSeparator className="bg-slate-700" />
              
              {/* Accesos de Administración */}
              {user?.role === 'school_admin' && (
                <>
                  <DropdownMenuItem
                    onClick={() => navigate('/school-admin')}
                    className="gap-2 cursor-pointer text-sm hover:bg-slate-700 focus:bg-slate-700 font-bold text-amber-400"
                  >
                    <LayoutDashboard size={14} strokeWidth={2.5} />
                    Panel Institucional
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-700" />
                </>
              )}

              {user?.role === 'admin' && (
                <>
                  <DropdownMenuItem
                    onClick={() => navigate('/admin')}
                    className="gap-2 cursor-pointer text-sm hover:bg-slate-700 focus:bg-slate-700 font-bold text-emerald-400"
                  >
                    <ShieldAlert size={14} strokeWidth={2.5} />
                    Panel de CEO
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-700" />
                </>
              )}

              {/* Opciones Generales */}
              
              {/* NUEVO: Botón del Tutorial */}
              {onStartTour && (
                  <DropdownMenuItem
                    onClick={onStartTour}
                    className="gap-2 cursor-pointer text-sm hover:bg-slate-700 focus:bg-slate-700 font-medium text-amber-300"
                  >
                    <Sparkles size={14} strokeWidth={2} className="text-amber-400" />
                    Tutorial de uso
                  </DropdownMenuItem>
              )}

              <DropdownMenuItem
                onClick={() => setShowProfile(true)}
                className="gap-2 cursor-pointer text-sm hover:bg-slate-700 focus:bg-slate-700 font-medium"
              >
                <UserCog size={14} strokeWidth={2} className="text-blue-400" />
                Mi Perfil
              </DropdownMenuItem>
              
              {!user?.institution_name && (
                <DropdownMenuItem
                  onClick={onShowPricing}
                  className="gap-2 cursor-pointer text-sm hover:bg-slate-700 focus:bg-slate-700 font-medium"
                >
                  <CreditCard size={14} strokeWidth={2} className="text-violet-400" />
                  Ver planes
                </DropdownMenuItem>
              )}
              
              <DropdownMenuSeparator className="bg-slate-700" />
              
              <DropdownMenuItem
                onClick={logout}
                className="gap-2 cursor-pointer text-sm text-red-400 hover:text-red-300 hover:bg-slate-700 focus:bg-slate-700 font-medium"
              >
                <LogOut size={14} strokeWidth={2} />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <ProfileModal 
        open={showProfile} 
        onClose={() => setShowProfile(false)} 
      />
    </>
  );
}
